import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStatus from "../../hooks/useAuthStatus";
import { getTransactionHistory } from "../../api/apiServices";
import { TxIconCell } from "../../utils/txIconHelper";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

const FILTERS = ["All", "Buy", "Sell", "Deposit", "Withdraw", "Bills"];

function TxSkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="bg-surface border border-border rounded-2xl px-4 py-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-skeleton animate-pulse flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-28 bg-skeleton rounded-lg animate-pulse" />
            <div className="h-2.5 w-20 bg-skeleton rounded-lg animate-pulse" />
          </div>
          <div className="space-y-2 flex flex-col items-end">
            <div className="h-3 w-20 bg-skeleton rounded-lg animate-pulse" />
            <div className="h-2.5 w-14 bg-skeleton rounded-lg animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

function getTxLabel(tx) {
  if (tx.type === "bill_payment") return tx.assetName || tx.asset || "Bill Payment";
  const labels = {
    buy: "Bought", sell: "Sold", deposit: "Deposited",
    withdraw_crypto: "Withdrew Crypto", withdrawal: "Withdrew Crypto",
    withdraw_naira: "Withdrew NGN",
  };
  const label = labels[tx.type] || tx.type;
  return tx.asset && tx.asset !== "NGN" ? `${label} ${tx.asset}` : label;
}

export default function Transactions() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery]   = useState("");
  const [refreshing, setRefreshing]     = useState(false);

  const fetchTransactions = async () => {
    if (!isAuthenticated) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await getTransactionHistory();
      if (res.status === true) { setTransactions(res.data); setError(null); }
      else setError("Failed to load transactions");
    } catch { setError("Unable to fetch transaction history"); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchTransactions(); }, [isAuthenticated]);

  const filterCounts = useMemo(() => ({
    All:      transactions.length,
    Buy:      transactions.filter(t => t.type === "buy").length,
    Sell:     transactions.filter(t => t.type === "sell").length,
    Deposit:  transactions.filter(t => t.type === "deposit").length,
    Withdraw: transactions.filter(t => ["withdraw_crypto","withdrawal","withdraw_naira"].includes(t.type)).length,
    Bills:    transactions.filter(t => t.type === "bill_payment").length,
  }), [transactions]);

  const filteredTransactions = useMemo(() => {
    let result = [...transactions];

    if (activeFilter !== "All") {
      result = result.filter(tx => {
        if (activeFilter === "Buy")      return tx.type === "buy";
        if (activeFilter === "Sell")     return tx.type === "sell";
        if (activeFilter === "Deposit")  return tx.type === "deposit";
        if (activeFilter === "Withdraw") return ["withdraw_crypto","withdrawal","withdraw_naira"].includes(tx.type);
        if (activeFilter === "Bills")    return tx.type === "bill_payment";
        return true;
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(tx =>
        tx.asset?.toLowerCase().includes(q)              ||
        tx.assetName?.toLowerCase().includes(q)          ||
        tx.paystackReference?.toLowerCase().includes(q)  ||
        tx.accountNumber?.toLowerCase().includes(q)      ||
        tx.bankName?.toLowerCase().includes(q)           ||
        tx.metadata?.phone?.includes(q)
      );
    }

    return result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [transactions, activeFilter, searchQuery]);

  return (
    <div className="min-h-screen bg-bg pb-28">
      <div className="bg-surface px-5 pt-12 pb-4 flex items-center gap-3 border-b border-border">
        <button onClick={() => navigate("/home")}
          className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center hover:opacity-80">
          <i className="ri-arrow-left-line text-secondary text-lg" />
        </button>
        <h1 className="text-primary font-bold text-lg font-display">
          Transaction History
        </h1>
        {!isAuthenticated && (
          <button onClick={() => navigate("/auth")}
            className="ml-auto px-5 py-2 text-sm font-semibold text-brand border border-brand-border rounded-xl hover:bg-brand-light transition-colors">
            Sign In
          </button>
        )}
      </div>

      {isAuthenticated && (
        <div className="px-5 pt-4 pb-1">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-3.5 text-muted text-base" />
            <input
              type="text"
              placeholder="Search asset, network, phone, reference…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-surface border border-border-strong rounded-2xl py-3 pl-10 pr-4 text-sm text-primary placeholder:text-faint focus:outline-none focus:border-brand transition-colors"
            />
          </div>
        </div>
      )}

      <div className="flex gap-2 px-5 py-3 overflow-x-auto no-scrollbar">
        {FILTERS.map(f => {
          const count  = filterCounts[f];
          const active = activeFilter === f && isAuthenticated;
          return (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              disabled={!isAuthenticated}
              className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all ${!isAuthenticated ? "opacity-40 cursor-not-allowed" : ""}`}
              style={{
                background: active ? "var(--color-brand)" : "var(--color-surface)",
                color:      active ? "#fff" : "var(--color-text-muted)",
                border:     active ? "none" : "1px solid var(--color-border-strong)",
              }}
            >
              {f}
              {isAuthenticated && count > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: active ? "rgba(255,255,255,0.2)" : "var(--color-surface-hover)", color: active ? "#fff" : "var(--color-text-secondary)" }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="px-5 space-y-3">
        {!isAuthenticated ? (
          <div className="min-h-[400px] flex items-center justify-center bg-surface rounded-3xl border border-border mt-2">
            <div className="text-center px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-hover flex items-center justify-center">
                <i className="ri-lock-2-line text-muted text-3xl" />
              </div>
              <p className="font-semibold text-secondary text-base mb-1">Login to view transactions</p>
              <button onClick={() => navigate("/auth")}
                className="px-8 py-3 bg-brand text-on-brand font-semibold rounded-2xl text-sm mt-4">
                Sign In
              </button>
            </div>
          </div>
        ) : loading && !refreshing ? (
          <TxSkeleton />
        ) : error ? (
          <div className="text-center py-16" style={{ color: "var(--color-danger)" }}>
            <p>{error}</p>
            <button onClick={fetchTransactions} className="mt-4 text-brand font-medium">Retry</button>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-16 bg-surface rounded-2xl border border-border mt-2">
            <i className="ri-file-list-3-line text-faint text-5xl mb-3 block" />
            <p className="text-muted text-sm">No transactions found</p>
            {activeFilter !== "All" && (
              <button onClick={() => setActiveFilter("All")} className="mt-2 text-brand text-sm font-semibold">
                Show all
              </button>
            )}
          </div>
        ) : (
          filteredTransactions.map(tx => (
            <div
              key={tx._id}
              onClick={() => navigate(`/transaction/${tx._id}`)}
              className="bg-surface border border-border rounded-2xl px-4 py-4 cursor-pointer hover:border-brand-border active:scale-[0.99] transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TxIconCell tx={tx} size="w-11 h-11" />

                  <div>
                    <p className="text-primary text-sm font-bold">{getTxLabel(tx)}</p>
                    <p className="text-muted text-xs">
                      {new Date(tx.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                      {tx.metadata?.phone && <span className="font-mono"> · {tx.metadata.phone}</span>}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-primary text-sm font-bold">
                    {["sell","withdraw_naira","withdrawal","withdraw_crypto"].includes(tx.type) ? "−" : ""}
                    ₦{(tx.nairaAmount || 0).toLocaleString()}
                  </p>
                  {tx.cryptoAmount > 0 && (
                    <p className="text-muted text-xs">
                      {["sell","withdraw_crypto","withdrawal"].includes(tx.type) ? "−" : "+"}
                      {tx.cryptoAmount} {tx.asset}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <span className="text-xs px-2.5 py-1 rounded-full font-semibold"
                  style={{
                    background: ["completed","delivered"].includes(tx.status) ? "var(--color-success-light)" :
                                ["pending","processing"].includes(tx.status)  ? "var(--color-warning-light)" :
                                "var(--color-danger-light)",
                    color:      ["completed","delivered"].includes(tx.status) ? "var(--color-success-text)" :
                                ["pending","processing"].includes(tx.status)  ? "var(--color-warning-text)" :
                                "var(--color-danger-text)",
                  }}>
                  {tx.status === "delivered" ? "Delivered" : tx.status}
                </span>
                {(tx.paystackReference || tx.metadata?.requestId) && (
                  <span className="text-faint text-xs font-mono">
                    {(tx.paystackReference || tx.metadata?.requestId || "").slice(0, 16)}…
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <MobileBottomNav />
    </div>
  );
}
